/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedNode').BalancedNode} BalancedNode */

import { useMemo, useState } from 'react'
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { getGroupMateIds, canInsertAt } from '../../generic/utilities/objectStructures/balanced/balancedConstraints'
import { formatBalancedLabel } from '../../generic/utilities/objectStructures/balanced/balancedLabel'
import { COMPACT_SELECT_SX } from '../../generic/utilities/objectStructures/balanced/balancedStyles'

/**
 * Returns a new id array with `sourceId` moved before or after `targetId`.
 *
 * @param {string[]} ids - Current ordered ids
 * @param {string} sourceId - Id being moved
 * @param {string} targetId - Reference id to position against
 * @param {'before'|'after'} position - Side of the target to place the source
 * @returns {string[]} New ordered ids
 */
const reorder = (ids, sourceId, targetId, position) => {
  const withoutSource = ids.filter(id => id !== sourceId)
  const targetIdx = withoutSource.indexOf(targetId)
  const insertAt = position === 'before' ? targetIdx : targetIdx + 1
  const result = [...withoutSource]
  result.splice(insertAt, 0, sourceId)
  return result
}

/**
 * Returns a new id array with `sourceId` moved to a 0-based `index`, keeping the
 * relative order of the other ids unchanged.
 *
 * @param {string[]} ids - Current ordered ids
 * @param {string} sourceId - Id being moved
 * @param {number} index - 0-based insert index in the list without the source
 * @returns {string[]} New ordered ids
 */
const moveToIndex = (ids, sourceId, index) => {
  const withoutSource = ids.filter(id => id !== sourceId)
  const result = [...withoutSource]
  result.splice(index, 0, sourceId)
  return result
}

/**
 * Dialog to reposition a node within a column relative to another node.
 *
 * @param {Object}       props
 * @param {boolean}      props.open         - Whether the dialog is open
 * @param {Function}     props.onClose      - Called when the dialog is cancelled
 * @param {Function}     props.onAccept     - Called with the new ordered ID array on confirm
 * @param {BalancedNode} props.sourceNode   - The node being repositioned
 * @param {BalancedNode[]} props.columnNodes - All nodes in the column (ordered)
 */
const BalancedColumnReorderDialog = ({ open, onClose, onAccept, sourceNode, columnNodes }) => {
  const { t_ } = useSmartTranslation()
  const [targetNodeId, setTargetNodeId] = useState(null)
  const [position, setPosition] = useState('before')
  const [positionInput, setPositionInput] = useState('')

  const mates = sourceNode ? getGroupMateIds(sourceNode) : new Set()
  const otherNodes = (columnNodes ?? []).filter(n => n.id !== sourceNode?.id && mates.has(n.id))
  /**
   * Candidates offered in the "Reference node" selector, as option objects.
   *
   * Picking a node on screen means picking THAT node, not a text that resembles
   * it (RN10): two jobs in one station column can carry an identical label,
   * because a `code` is only unique within its workflow while a column mixes
   * jobs from several subworkflows. So the option carries the node identity and
   * the label travels beside it, as presentation.
   *
   * `name` is never displayed: it is the field SmartSelect derives each option's
   * React key from (`name ?? label ?? code`), so it has to stay unique even when
   * two labels are identical. The visible text comes from `optionLabelTransform`,
   * which never affects the internal value.
   *
   * Memoised so the selected option keeps referential identity between renders:
   * the generic selector compares option and value by reference.
   */
  const referenceOptions = useMemo(
    () => otherNodes.map(node => {
      const label = formatBalancedLabel(node.code, node.name)
      return { id: node.id, label, name: label + ' #' + node.id }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnNodes, sourceNode]
  )

  const selectedOption = referenceOptions.find(option => option.id === targetNodeId) ?? null
  const targetNode = otherNodes.find(n => n.id === targetNodeId) ?? null

  const columnSize = (columnNodes ?? []).length
  // Whether the user is placing the node by typing a position number.
  const positionActive = positionInput.trim() !== ''
  const typedPosition = parseInt(positionInput, 10)
  // Out-of-range values clamp silently to the nearest end (D1).
  const effectivePosition = Math.min(Math.max(typedPosition, 1), columnSize)
  const positionInsertIndex = effectivePosition - 1

  /**
   * Builds the proposed ordered ids for the active input method (position number
   * takes precedence), or null when no method has a complete input.
   *
   * @returns {string[]|null}
   */
  const buildNewIds = () => {
    if (!sourceNode) return null
    const allIds = (columnNodes ?? []).map(n => n.id)
    if (positionActive) {
      if (!Number.isFinite(typedPosition)) return null
      return moveToIndex(allIds, sourceNode.id, positionInsertIndex)
    }
    if (!targetNode) return null
    return reorder(allIds, sourceNode.id, targetNode.id, position)
  }

  // Whether the current input yields a valid arrangement (enables Accept).
  const proposedValid = (() => {
    const newIds = buildNewIds()
    if (!newIds) return false
    return canInsertAt(sourceNode, columnNodes, newIds.indexOf(sourceNode.id))
  })()

  // Show the dependency message only when a typed position cannot be honoured (S2).
  const positionConflict = positionActive && Number.isFinite(typedPosition) && !proposedValid

  /**
   * Confirms the reorder, emits the new id order and resets the form.
   *
   * @returns {void}
   */
  const handleAccept = () => {
    if (!proposedValid) return
    onAccept(buildNewIds())
    setTargetNodeId(null)
    setPosition('before')
    setPositionInput('')
  }

  /** Resets the form and closes the dialog without applying changes. @returns {void} */
  const handleCancel = () => {
    setTargetNodeId(null)
    setPosition('before')
    setPositionInput('')
    onClose()
  }

  return (
    <SmartDialog
      setOpen={open}
      title='default'
      acceptCallback={handleAccept}
      cancelCallback={handleCancel}
      accept={positionActive || !!targetNode}
      renderComponent={
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: '20rem' }}>
          <Typography variant='body2'>
            {t_('Reposition')}{': '}
            <strong>{formatBalancedLabel(sourceNode?.code, sourceNode?.name)}</strong>
          </Typography>
          <TextField
            id='balanced-reorder-position'
            type='number'
            size='small'
            label={t_('Position')}
            value={positionInput}
            onChange={e => setPositionInput(e.target.value)}
            inputProps={{ min: 1, max: columnSize }}
            error={positionConflict}
            helperText={positionConflict ? t_('This position breaks node dependencies') : ''}
          />
          <Box sx={COMPACT_SELECT_SX}>
            <SmartSelect
              label={t_('Reference node')}
              selectableOptions={referenceOptions}
              value={selectedOption}
              onChange={option => setTargetNodeId(option?.id ?? null)}
              optionLabelTransform={option => option?.label ?? ''}
              size='small'
              autoSelectFirst={false}
            />
          </Box>
          <FormControl>
            <RadioGroup
              row
              value={position}
              onChange={e => setPosition(e.target.value)}
            >
              <FormControlLabel
                value='before'
                control={<Radio />}
                label={<Typography variant='body2'>{t_('Before')}</Typography>}
              />
              <FormControlLabel
                value='after'
                control={<Radio />}
                label={<Typography variant='body2'>{t_('After')}</Typography>}
              />
            </RadioGroup>
          </FormControl>
        </div>
      }
    />
  )
}

export default BalancedColumnReorderDialog
